

export interface EntranceTracking {
  id: string;
  state: number;
  boss: number;
  reward: number;
  destination: string;
  era?: "adult" | "child";
}

export interface SubPath {
  exit: string;
  era: "child" | "adult" | "both";
  exitNotVisited: number;
}

export interface PathResult {
  paths: SubPath[];
  era: "adult" | "child" | "both";
}

export const findAllPaths = (
    entrances: EntranceTracking[],
    startArea: string | null,
    endArea: string | null
): PathResult[] => {
 if (startArea === null || endArea === null) {
        return [];
    }

    // Track shortest path length for each era
    const shortestPathLength: Record<"adult" | "child" | "both", number> = {
        adult: Infinity,
        child: Infinity,
        both: Infinity
    };

    // Store shortest paths for each era
    const shortestPaths: Record<"adult" | "child" | "both", PathResult[]> = {
        adult: [],
        child: [],
        both: []
    };

    const getDestinationArea = (dest: string): string => {
        return dest.split('(')[0].trim();
    };

    const getSourceArea = (id: string): string => {
        return id.split(/=>|⇒| /)[0].trim();
    };

    // BFS implementation for shortest paths
    const bfs = (era: "adult" | "child" | "both") => {
        // Queue of [area, path, visited]
        const queue: [string, SubPath[], Set<string>][] = [[startArea, [], new Set<string>()]];
        
        while (queue.length > 0) {
            const [currentArea, currentPath, visited] = queue.shift()!;
            
            // Skip if path is too long or longer than current shortest
            if (currentPath.length >= 32 || currentPath.length >= shortestPathLength[era]) {
                continue;
            }
            
            // If we reached the destination
            if (currentArea === endArea && currentPath.length > 0) {
                // If this is a shorter path than what we've found
                if (currentPath.length < shortestPathLength[era]) {
                    shortestPathLength[era] = currentPath.length;
                    shortestPaths[era] = [{ 
                        paths: currentPath,
                        era: era
                    }];
                } 
                // If it's the same length as the shortest path
                else if (currentPath.length === shortestPathLength[era]) {
                    shortestPaths[era].push({
                        paths: currentPath,
                        era: era
                    });
                }
                continue;
            }
            
            // Find all possible next steps
            const possibleMoves = entrances.filter(entrance => {
                if (getSourceArea(entrance.id) !== currentArea) return false;
                if (entrance.state !== 2) return false;
                if (era !== "both" && entrance.era && entrance.era !== era) return false;
                if (visited.has(entrance.id)) return false;
                return true;
            });
            
            for (const move of possibleMoves) {
                const nextArea = getDestinationArea(move.destination);
                const newVisited = new Set(visited).add(move.id);
                
                // Calculate exits not visited in the destination area
                const allExitsInNextArea = entrances.filter(entrance => {
                    if (getSourceArea(entrance.id) !== nextArea) return false;
                    if (entrance.state !== 2) return false;
                    if (era !== "both" && entrance.era && entrance.era !== era) return false;
                    return true;
                });
                const notVisitedExitsInNextArea = allExitsInNextArea.filter(e => !newVisited.has(e.id));
                
                const subPath: SubPath = {
                    exit: move.id,
                    era: move.era || "both",
                    exitNotVisited: notVisitedExitsInNextArea.length
                };
                
                queue.push([nextArea, [...currentPath, subPath], newVisited]);
            }
        }
    };
    
    // Run BFS for each era
    bfs("both");
    bfs("adult");
    bfs("child");
    
    // Combine all shortest paths
    return [
        ...shortestPaths.adult,
        ...shortestPaths.child,
        ...shortestPaths.both
    ];
};